import { faCheck, faEdit, faEye, faInfoCircle, faLock, faPaperPlane, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthUser } from '../../../classes/AuthUser';
import { IOfferRequest } from '../../../interfaces';
import { ETranslation } from '../../../translations/translation-keys';
import Button, { EButtonSize } from '../../UI/Button/Button';
import Table from '../../UI/Table/Table';
import TableTh from '../../UI/Table/TableTh/TableTh';
import { useTableSort } from '../../UI/Table/useTableSort';
import classes from './OfferRequestsTable.module.scss';

interface IProps {
  offerrequests: Array<IOfferRequest>
  onClick: (event: React.MouseEvent<HTMLTableRowElement | HTMLButtonElement>, id: string) => void;
  sortable?: boolean;
  containerClassName?: string;
  containerStyle?: CSSProperties;
  authUser: AuthUser;
  onOpenAiModal?: (offerRequest: IOfferRequest) => void;
}

const OfferRequestTable: React.FC<IProps> = ({ offerrequests, onClick, sortable, containerClassName, containerStyle, authUser, onOpenAiModal }) => {
  const { t } = useTranslation();
  const { isPrimary, isOperator, isMicro } = authUser;
  const isPrimaryOrOperator = isPrimary || isOperator;

  const enrichedOfferRequests = React.useMemo(() => {
    return (offerrequests || []).map((or) => {
      const views = or.viewCount ?? 0;
      const offers = or.offerCount ?? 0;
      return {
        ...or,
        displayName: [or.lastName, or.firstName].filter(Boolean).join(" ") || or.email || "",
        viewCount: views,
        offerCount: offers,
        competitionScore: offers * 1000 + views,
      };
    });
  }, [offerrequests]);

  const { sort, onSort, items: sortedOfferRequests } = useTableSort({
    data: enrichedOfferRequests,
    defaultSort: { key: "created", reverse: true, type: "date" }
  });

  const renderCompetitionCell = (offerRequest: IOfferRequest) => {
    const viewCount = offerRequest.viewCount ?? 0;
    const offerCount = offerRequest.offerCount ?? 0;

    const offerColor = offerCount > 0 ? (offerCount >= 3 ? '#b45309' : '#1e293b') : '#16a34a';
    const offerIconColor = offerCount > 0 ? (offerCount >= 3 ? '#d97706' : '#2563eb') : '#16a34a';

    return (
      <div 
        style={{ display: 'flex', flexDirection: 'column', gap: '2px', whiteSpace: 'nowrap' }}
        title={`${offerCount} yritystä tarjonnut, ${viewCount} yritystä katsellut`}
      >
        <span style={{ 
          fontSize: '0.8rem', 
          fontWeight: 600, 
          color: offerColor, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '5px' 
        }}>
          <FontAwesomeIcon 
            icon={faPaperPlane} 
            style={{ 
              fontSize: '0.7rem', 
              color: offerIconColor,
              width: '12px',
              flexShrink: 0
            }} 
          />
          {offerCount === 0 ? 'Ei tarjouksia' : `${offerCount} ${offerCount === 1 ? 'tarjous' : 'tarjousta'}`}
        </span>
        <span style={{ 
          fontSize: '0.75rem', 
          color: '#64748b', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '5px' 
        }}>
          <FontAwesomeIcon 
            icon={faEye} 
            style={{ 
              fontSize: '0.7rem', 
              color: '#94a3b8',
              width: '12px',
              flexShrink: 0
            }} 
          />
          {viewCount} katsellut
        </span>
      </div>
    );
  };

  const renderOfferCell = (offerRequest: IOfferRequest) => {
    let badgeContent = null;
    if (offerRequest.hasArchivedOffer) {
      badgeContent = (
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', backgroundColor: '#e2e8f0', padding: '2px 8px', borderRadius: '4px', display: 'inline-block' }}>
          Ei tarjota
        </span>
      );
    } else if (offerRequest.offerStatus) {
      let statusLabel = "";
      let color = "#475569";
      let bg = "#f1f5f9";
      
      if (offerRequest.offerStatus === "DRAFT") {
        statusLabel = "Luonnos";
        color = "#475569";
        bg = "#e2e8f0";
      } else if (offerRequest.offerStatus === "SENT") {
        statusLabel = "Lähetetty";
        color = "#2563eb";
        bg = "#dbeafe";
      } else if (offerRequest.offerStatus === "WON") {
        statusLabel = "Voitettu";
        color = "#16a34a";
        bg = "#dcfce7";
      } else if (offerRequest.offerStatus === "LOST") {
        statusLabel = "Ei tilannut";
        color = "#dc2626";
        bg = "#fee2e2";
      }
      
      const priceText = offerRequest.offerPrice !== undefined ? ` (${offerRequest.offerPrice} €)` : "";
      badgeContent = (
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: color, backgroundColor: bg, padding: '2px 8px', borderRadius: '4px', display: 'inline-block' }}>
          {statusLabel}{priceText}
        </span>
      );
    } else {
      badgeContent = <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>Ei aloitettu</span>;
    }

    const canEditOffer = !isMicro || authUser.hasOfferRequestAccess(offerRequest.id);

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        {badgeContent}
        {canEditOffer && onOpenAiModal && (
          <button
            type="button"
            title="AI-tarjous ja esikatselu"
            data-testid={`ai-offer-modal-btn-${offerRequest.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenAiModal(offerRequest);
            }}
            style={{
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              padding: '4px 7px',
              cursor: 'pointer',
              color: '#475569',
              fontSize: '0.8rem',
              display: 'inline-flex',
              alignItems: 'center',
              lineHeight: 1
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e2512c';
              e.currentTarget.style.color = '#ffffff';
              e.currentTarget.style.borderColor = '#e2512c';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#475569';
              e.currentTarget.style.borderColor = '#cbd5e1';
            }}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
        )}
      </div>
    );
  };

  const renderAttributionCell = (attribution?: string) => {
    if (!attribution) return null;
    
    let color = "#475569";
    let bg = "#f1f5f9";
    
    if (attribution === "DIRECT") {
      color = "#0f766e";
      bg = "#ccfbf1";
    } else if (attribution === "AI") {
      color = "#6d28d9";
      bg = "#f3e8ff";
    } else if (attribution === "PARTNERS") {
      color = "#1d4ed8";
      bg = "#dbeafe";
    } else if (attribution === "SEARCH") {
      color = "#b45309";
      bg = "#fef3c7";
    } else if (attribution === "OTHER") {
      color = "#6b7280";
      bg = "#f3f4f6";
    }
    
    return (
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: color, backgroundColor: bg, padding: '2px 8px', borderRadius: '4px', display: 'inline-block' }}>
        {attribution}
      </span>
    );
  };

  const renderMatchStatusCell = (matchStatus?: string) => {
    if (!matchStatus) return null;

    let label = "Vaatii huomiota";
    let color = "#dc2626";
    let bg = "#fee2e2";

    if (matchStatus === "MATCHED") {
      label = "Käsitelty";
      color = "#16a34a";
      bg = "#dcfce7";
    } else if (matchStatus === "SIGNALED_MORE") {
      label = "Lisää toivottu";
      color = "#b45309";
      bg = "#fef3c7";
    } else if (matchStatus === "IN_VIEWING") {
      label = "Katseltu";
      color = "#2563eb";
      bg = "#dbeafe";
    } else if (matchStatus === "NEEDS_ATTENTION") {
      label = "Vaatii huomiota";
      color = "#dc2626";
      bg = "#fee2e2";
    }

    return (
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: color, backgroundColor: bg, padding: '2px 8px', borderRadius: '4px', display: 'inline-block', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    );
  };

  const formatFullDateTime = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} klo ${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  };

  const formatRelativeTime = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHr = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHr / 24);

      if (diffSec < 60) {
        return 'juuri nyt';
      } else if (diffMin < 60) {
        return `${diffMin} min sitten`;
      } else if (diffHr < 24) {
        return `${diffHr} t sitten`;
      } else if (diffDays === 1) {
        return 'eilen';
      } else {
        return `${diffDays} pv sitten`;
      }
    } catch (e) {
      return '';
    }
  };

  const [showCompetitionInfo, setShowCompetitionInfo] = useState(false);
  const infoRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!showCompetitionInfo) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(event.target as Node)) {
        setShowCompetitionInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCompetitionInfo]);

  const renderCompetitionHeaderContent = () => (
    <span style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
      <span>{t(ETranslation.COMMON_COMPETITION)}</span>
      <span
        ref={infoRef}
        onClick={(e) => {
          e.stopPropagation();
          setShowCompetitionInfo((prev) => !prev);
        }}
        style={{
          marginLeft: '6px',
          cursor: 'pointer',
          color: showCompetitionInfo ? '#2563eb' : '#94a3b8',
          fontSize: '0.8rem',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
        title="Lisätietoa kilpailutilanteesta"
      >
        <FontAwesomeIcon icon={faInfoCircle} />
        {showCompetitionInfo && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              width: '270px',
              backgroundColor: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              padding: '0.75rem 0.85rem',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              fontSize: '0.75rem',
              lineHeight: 1.45,
              color: '#1e293b',
              fontWeight: 400,
              textAlign: 'left',
              cursor: 'default',
              whiteSpace: 'normal',
              wordBreak: 'normal',
              overflowWrap: 'normal',
              hyphens: 'none',
              textTransform: 'none',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
              <strong style={{ color: '#0f172a', fontSize: '0.75rem' }}>Huom!</strong>
              <span
                onClick={() => setShowCompetitionInfo(false)}
                style={{
                  cursor: 'pointer',
                  color: '#94a3b8',
                  fontSize: '0.85rem',
                  lineHeight: 1,
                  padding: '0 2px',
                }}
              >
                ✕
              </span>
            </div>
            <div style={{ wordBreak: 'normal', overflowWrap: 'normal', whiteSpace: 'normal', hyphens: 'none' }}>
              Tarjousten määrään lasketaan HankiX:n kautta tehdyt tarjoukset, ei esim. asiakkaan ja yrityksen väliset yksityissähköpostit.
            </div>
          </div>
        )}
      </span>
    </span>
  );

  return (
    <div className={containerClassName} style={containerStyle}>
      <Table hover sortContext={{ sort, onSort }}>
        <thead>
          <tr className={classes.TableHeader}>
            {isMicro && <th></th>}
            {sortable ? (
              <>
                <TableTh sort="created" type="date">{t(ETranslation.COMMON_CREATED)}</TableTh>
                {isMicro && <TableTh sort="competitionScore" type="int" numeric>{renderCompetitionHeaderContent()}</TableTh>}
                <TableTh sort="service.name">{t(ETranslation.COMMON_SERVICE)}</TableTh>
                <TableTh sort="displayName">Nimi</TableTh>
                <TableTh sort="address">Osoite</TableTh>
                <TableTh sort="distanceKm">Etäisyys</TableTh>
                <TableTh sort="email">Yhteystiedot</TableTh>
                {isPrimaryOrOperator && <TableTh sort="matchStatus">Status</TableTh>}
                {isPrimaryOrOperator && <TableTh sort="attribution">Lähde</TableTh>}
                {isPrimary && <TableTh sort="operator.name">{t(ETranslation.COMMON_OPERATOR_NAME)}</TableTh>}
                {!isPrimary && <TableTh sort="offerStatus">Tarjous</TableTh>}
                {isPrimary && <TableTh sort="archived" type="boolean">{t(ETranslation.COMMON_ARCHIVED)}</TableTh>}
              </>
            ) : (
              <>
                <th>{t(ETranslation.COMMON_CREATED)}</th>
                {isMicro && <th>{renderCompetitionHeaderContent()}</th>}
                <th>{t(ETranslation.COMMON_SERVICE)}</th>
                <th>Nimi</th>
                <th>Osoite</th>
                <th>Etäisyys</th>
                <th>Yhteystiedot</th>
                {isPrimaryOrOperator && <th>Status</th>}
                {isPrimaryOrOperator && <th>Lähde</th>}
                {isPrimary && <th>{t(ETranslation.COMMON_OPERATOR_NAME)}</th>}
                {!isPrimary && <th>Tarjous</th>}
                {isPrimary && <th>{t(ETranslation.COMMON_ARCHIVED)}</th>}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedOfferRequests.map(offerRequest => {
            return (
              <tr 
                key={offerRequest.id} 
                onClick={(e) => onClick(e, offerRequest.id)}
                style={offerRequest.hasArchivedOffer ? { opacity: 0.55, backgroundColor: '#f8fafc', color: '#64748b' } : {}}
              >
                {isMicro && (
                  <td>{authUser.hasOfferRequestAccess(offerRequest.id) ? (
                     <div className={classes.UnlockedBadge}>
                       <FontAwesomeIcon icon={faUnlock} />
                       <span>Ostettu</span>
                     </div>
                  ) : (
                    <Button onClick={(e) => onClick(e, offerRequest.id)} size={EButtonSize.SMALL}>
                      <FontAwesomeIcon icon={faLock} style={{ marginRight: '6px' }} />
                      {t(ETranslation.COMMON_BUY)}
                    </Button>
                  )}</td>
                )}
                <td style={{ verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
                      {offerRequest.created && formatFullDateTime(offerRequest.created)}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {!isPrimary && !offerRequest.offerStatus && !offerRequest.hasArchivedOffer && (
                        <span style={{ 
                          fontSize: '0.7rem', 
                          fontWeight: 700, 
                          color: '#b45309', 
                          backgroundColor: '#fef3c7', 
                          padding: '1px 6px', 
                          borderRadius: '12px', 
                          border: '1px solid #fde68a',
                          marginRight: '2px'
                        }}>
                          uusi
                        </span>
                      )}
                      {offerRequest.created && formatRelativeTime(offerRequest.created)}
                    </span>
                    {offerRequest.moreOffersRequestedAt && (
                      <span style={{ 
                        fontSize: '0.7rem', 
                        fontWeight: 700, 
                        color: '#78350f', 
                        backgroundColor: '#fef3c7', 
                        padding: '2px 6px', 
                        borderRadius: '12px', 
                        border: '1px solid #fde68a',
                        marginTop: '2px',
                        display: 'inline-block',
                        width: 'fit-content'
                      }}>
                        ⚡ Asiakas toivoo lisää tarjouksia
                      </span>
                    )}
                  </div>
                </td>
                {isMicro && (
                  <td style={{ verticalAlign: 'middle' }}>
                    {renderCompetitionCell(offerRequest)}
                  </td>
                )}
                <td>{offerRequest.service?.name}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {isMicro && !authUser.hasOfferRequestAccess(offerRequest.id) && (
                      <FontAwesomeIcon icon={faLock} style={{ color: '#94a3b8', fontSize: '0.8rem' }} />
                    )}
                    <span>
                      {offerRequest.lastName ? `${offerRequest.lastName}, ${offerRequest.firstName || ""}` : (offerRequest.firstName || "")}
                    </span>
                  </div>
                </td>
                <td>
                  {[
                    offerRequest.address,
                    [offerRequest.zip, offerRequest.city].filter(Boolean).join(' ') + 
                      (offerRequest.municipality && offerRequest.municipality !== offerRequest.city ? ` (${offerRequest.municipality})` : '')
                  ].filter(Boolean).join(', ')}
                </td>
                <td>{offerRequest.distanceKm != null ? `${offerRequest.distanceKm} km` : ''}</td>
                <td style={{ verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '0.85rem', color: '#1e293b' }}>{offerRequest.email}</span>
                    {offerRequest.phone && <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{offerRequest.phone}</span>}
                  </div>
                </td>
                {isPrimaryOrOperator && <td style={{ verticalAlign: 'middle' }}>{renderMatchStatusCell(offerRequest.matchStatus)}</td>}
                {isPrimaryOrOperator && <td style={{ verticalAlign: 'middle' }}>{renderAttributionCell(offerRequest.attribution)}</td>}
                {isPrimary && <td>{offerRequest?.operator?.name}</td>}
                {!isPrimary && <td>{renderOfferCell(offerRequest)}</td>}
                {isPrimary && (
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <span>{offerRequest.archived ? <FontAwesomeIcon icon={faCheck} /> : ""}</span>
                      {onOpenAiModal && (
                        <button
                          type="button"
                          title="AI-tarjous ja esikatselu"
                          data-testid={`ai-offer-modal-btn-${offerRequest.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenAiModal(offerRequest);
                          }}
                          style={{
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            padding: '4px 7px',
                            cursor: 'pointer',
                            color: '#475569',
                            fontSize: '0.8rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            lineHeight: 1
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e2512c';
                            e.currentTarget.style.color = '#ffffff';
                            e.currentTarget.style.borderColor = '#e2512c';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                            e.currentTarget.style.color = '#475569';
                            e.currentTarget.style.borderColor = '#cbd5e1';
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default OfferRequestTable;
